import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { BookOpen, CloudArrowUp, EnvelopeSimple, FileText, Lifebuoy, Package } from "@phosphor-icons/react";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui";
import { PressEyebrow, PressPanel, StatusPill } from "@/components/pullpress/PressVisuals";

type SupportTopic = {
  readonly title: string;
  readonly body: string;
  readonly icon: typeof CloudArrowUp;
};

const TOPICS: readonly SupportTopic[] = [
  {
    title: "Bring in the source.",
    body: "Use notes, files, recordings, or drafts.",
    icon: CloudArrowUp,
  },
  {
    title: "Judge the proof.",
    body: "Check the chapters, editor notes, and approval status.",
    icon: BookOpen,
  },
  {
    title: "Protect the packet.",
    body: "Check the manuscript, EPUB, PDF, and book details.",
    icon: Package,
  },
];

export function SupportPage() {
  return (
    <AppShell variant="public">
      <section className="border-b border-rule">
        <div className="mx-auto grid max-w-[1120px] gap-6 px-4 py-8 sm:px-6 lg:grid-cols-[0.82fr_1fr] lg:px-8">
          <div>
            <PressEyebrow>The book should not stay stuck.</PressEyebrow>
            <h1 className="mt-4 max-w-[620px] font-display text-[clamp(2.2rem,4.2vw,4rem)] font-semibold leading-[0.98] tracking-as-tight text-ink-1">
              Get the book moving again.
            </h1>
            <p className="mt-4 max-w-[560px] font-text text-base leading-as-read text-ink-2">
              Ask for help with sign in, upload, proofing, editor handoff, final files, or release packets.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link to="/contact?intent=support">
                <Button variant="walnut" size="lg">Send the problem</Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="secondary" size="lg" type="button">Go to books</Button>
              </Link>
            </div>
          </div>
          <PressPanel className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <PressEyebrow>Before you write.</PressEyebrow>
                <h2 className="mt-3 font-display text-2xl font-semibold leading-tight text-ink-1">
                  Four details help us move the book faster.
                </h2>
              </div>
              <StatusPill tone="ready">2026</StatusPill>
            </div>
            <div className="mt-5 divide-y divide-rule-faint">
              <SupportCheck label="Confirm the email address on the account." />
              <SupportCheck label="Note the book title and current stage." />
              <SupportCheck label="Name the file type or release step involved." />
              <SupportCheck label="Include the exact error or missing output if one appears." />
            </div>
          </PressPanel>
        </div>
      </section>

      <section className="border-b border-rule bg-paper-raised">
        <div className="mx-auto max-w-[1120px] px-4 py-8 sm:px-6 lg:px-8">
          <PressEyebrow>Common support paths.</PressEyebrow>
          <div className="mt-5 grid gap-3 md:grid-cols-3">
            {TOPICS.map(({ title, body, icon: Icon }) => (
              <PressPanel key={title} className="p-4">
                <Icon size={22} className="text-walnut" />
                <h2 className="mt-3 font-display text-xl font-semibold leading-tight text-ink-1">{title}</h2>
                <p className="mt-2 font-text text-sm leading-as-read text-ink-2">{body}</p>
              </PressPanel>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-rule">
        <div className="mx-auto grid max-w-[1120px] gap-4 px-4 py-8 sm:px-6 md:grid-cols-2 lg:px-8">
          <SupportDoc
            icon={<EnvelopeSimple size={22} />}
            title="Account access"
            body="Sign in with email or Google. Password resets go to the email on file."
          />
          <SupportDoc
            icon={<FileText size={22} />}
            title="Release boundary"
            body="PullPress prepares the packet. Store uploads, ISBNs, and approvals happen in your publishing account."
          />
        </div>
      </section>
    </AppShell>
  );
}

function SupportCheck({ label }: { label: string }) {
  return (
    <div className="flex items-start gap-3 py-3">
      <Lifebuoy size={18} className="mt-0.5 shrink-0 text-walnut" />
      <p className="font-text text-sm leading-as-read text-ink-2">{label}</p>
    </div>
  );
}

function SupportDoc({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <PressPanel className="p-4">
      <div className="text-walnut">{icon}</div>
      <h2 className="mt-3 font-display text-xl font-semibold leading-tight text-ink-1">{title}</h2>
      <p className="mt-2 font-text text-sm leading-as-read text-ink-2">{body}</p>
    </PressPanel>
  );
}
